import { useAuthStore } from '@/store/auth';
import { useModuleStore } from '@/store/module';
import Router from '@/router';
import { getUserGroups } from '@/api/user-management';

/**
 * Helper function to find a module by ID
 */
function findModuleById(modules, moduleId) {
  if (!modules || !modules.length || !moduleId) return null;

  // First check top-level modules
  const module = modules.find((m) => parseInt(m.id) === parseInt(moduleId));
  if (module) return module;

  // Then check children_recursive
  for (const parentModule of modules) {
    if (
      parentModule.children_recursive &&
      parentModule.children_recursive.length
    ) {
      const childModule = parentModule.children_recursive.find(
        (child) => parseInt(child.id) === parseInt(moduleId)
      );
      if (childModule) return childModule;

      // Check grandchildren (deeper nesting)
      for (const child of parentModule.children_recursive) {
        if (child.children_recursive && child.children_recursive.length) {
          const grandchildModule = child.children_recursive.find(
            (grandchild) => parseInt(grandchild.id) === parseInt(moduleId)
          );
          if (grandchildModule) return grandchildModule;
        }
      }
    }
  }

  return null;
}

/**
 * Check if user has permission for a specific module and action
 */
function checkUserPermission(user, moduleId, action, module) {
  // Super admin has all permissions
  if (user?.users_group_id === 1) {
    return true;
  }

  // Check user-specific override permissions first
  if (user?.overwrite_permissions) {
    try {
      const userPermissions =
        typeof user.overwrite_permissions === 'string'
          ? JSON.parse(user.overwrite_permissions)
          : user.overwrite_permissions;

      if (
        Array.isArray(userPermissions[action]) &&
        userPermissions[action].includes(parseInt(moduleId))
      ) {
        return true;
      }
    } catch (e) {
      console.error('Error parsing overwrite_permissions:', e);
    }
  }

  // Check user group permissions
  if (user?.group_permissions) {
    if (
      Array.isArray(user.group_permissions[action]) &&
      user.group_permissions[action].includes(parseInt(moduleId))
    ) {
      return true;
    }
  }

  // Check module permissions as fallback
  if (module?.permissions) {
    // Format 1: Check if permissions is an array of strings
    if (Array.isArray(module.permissions)) {
      return module.permissions.includes(action);
    }

    // Format 2: Check if module has permissions by group
    if (
      module.permissions[action] &&
      Array.isArray(module.permissions[action])
    ) {
      return module.permissions[action].includes(
        parseInt(user?.users_group_id)
      );
    }
  }

  return false;
}

/**
 * Load group permissions if needed - exported for use in other files
 */
export async function ensureGroupPermissions(user) {
  // Skip if already loaded or not needed
  if (
    !user ||
    user.users_group_id === 1 ||
    user.overwrite_permissions ||
    user.group_permissions
  ) {
    // console.log('Skipping group permissions loading because:', {
    //   noUser: !user,
    //   isSuperAdmin: user?.users_group_id === 1,
    //   hasOverwritePermissions: !!user?.overwrite_permissions,
    //   hasGroupPermissions: !!user?.group_permissions,
    // });
    return;
  }

  try {
    // console.log(
    //   'Loading group permissions for user group ID:',
    //   user.users_group_id
    // );
    const userGroupData = await getUserGroups(`id=${user.users_group_id}`);
    // console.log('Loaded user group data:', userGroupData);

    if (userGroupData?.permissions) {
      // console.log(
      //   'Found group permissions in user group data:',
      //   userGroupData.permissions
      // );
      useAuthStore().$patch({
        user: {
          ...user,
          group_permissions: userGroupData.permissions,
        },
      });
      // console.log('Updated user with group_permissions');
    } else {
      // console.log('No permissions found in user group data');
    }
  } catch (error) {
    console.error('Error loading group permissions:', error);
  }
}

/**
 * Middleware to check if user has the required permissions to access a route
 *
 * Usage in routes:
 * {
 *   path: '/some-path',
 *   name: 'SomePath',
 *   component: SomeComponent,
 *   meta: {
 *     permissions: {
 *       // Specify module ID
 *       moduleId: 5,
 *
 *       // Required actions: 'view', 'create', 'alter', 'drop'
 *       actions: ['view']
 *     }
 *   }
 * }
 */
export default async ({ to, next }) => {
  // Skip permission check for routes that don't require it
  if (to.meta.skipPermissionCheck || !to.meta.permissions) {
    return next();
  }

  const { user } = useAuthStore();
  const modules = useModuleStore().modules;
  const { moduleId, actions = ['view'] } = to.meta.permissions;

  // Super admin always has access
  if (user?.users_group_id === 1) {
    return next();
  }

  // Ensure we have the module ID and modules are loaded
  if (!moduleId || !modules || modules.length === 0) {
    return next({ path: '/unauthorized' });
  }

  // Find the module
  const module = findModuleById(modules, moduleId);
  if (!module) {
    return next({ path: '/unauthorized' });
  }

  // Ensure group permissions are loaded when needed
  await ensureGroupPermissions(user);

  // Check all required permissions
  const hasAllPermissions = actions.every((action) =>
    checkUserPermission(user, moduleId, action, module)
  );

  if (hasAllPermissions) {
    return next();
  } else {
    return next({ path: '/unauthorized' });
  }
};
